import { Color } from '@fe/core';
export type Font = `${number}px ${string}`;

export enum ChartPosition {
    top = 'top',
    right = 'right',
    bottom = 'bottom',
    left = 'left'
}

export enum LineStyle {
    solid,
    dashed
}

export type Point = [number, number];

export type Range = {
    min: number;
    max: number;
};

export type Size = {
    width: number;
    height: number;
};

export type TextProps = {
    alignment?: CanvasTextAlign;
    baseline?: CanvasTextBaseline;
    font?: string;
};

export type StrokeProps = {
    color: Color;
    width?: number;
    style?: LineStyle;
};

export type FillProps = {
    color: Color;
};
export type TextRectProps = {
    textColor: Color;
    fillColor: Color;
    padding: number;
};

export class Rect {
    private readonly _cords: [number, number, number, number];
    public readonly width: number;
    public readonly height: number;

    constructor(leftTop: Point, rightBottom: Point);
    constructor(p: Point, size: Size);
    constructor(arg1: Point, arg2: Size | Point) {
        if ('width' in arg2 && 'height' in arg2) {
            this._cords = [arg1[0], arg1[1], arg1[0] + arg2.width, arg1[1] + arg2.height];
            this.width = arg2.width;
            this.height = arg2.height;
        } else {
            this._cords = [arg1[0], arg1[1], arg2[0], arg2[1]];
            this.width = arg2[0] - arg1[0];
            this.height = arg2[1] - arg1[1];
        }
    }

    get left() {
        return this._cords[0];
    }

    get top() {
        return this._cords[1];
    }

    get right() {
        return this._cords[2];
    }

    get bottom() {
        return this._cords[3];
    }

    contains(x: number, y: number): boolean {
        return x >= this.left && x <= this.right && y >= this.top && y <= this.bottom;
    }

    translate(dx: number, dy: number): Rect {
        return new Rect([this.left + dx, this.top + dy], { width: this.width, height: this.height });
    }

    resize(dw: number, dh: number): Rect {
        return new Rect([this.left, this.top], { width: this.width + dw, height: this.height + dh });
    }
}

export function roundToSteps(value: number, step: number) {
    return Math.ceil(value / step) * step;
}

export function getTextHeight(font: string): number {
    const matches = font.match(/(\d{1,2})px/) ?? [];
    return +(matches[1] ?? '10');
}

// The max range being multiplicity of the step, that is included in the given range
export function roundRange(range: Range, step: number): Range {
    const { min, max } = range;
    return {
        min: Math.ceil(min / step) * step,
        max: Math.floor(max / step) * step
    };
}
